import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  User, Package, Heart, MapPin, Award, Settings, LogOut, 
  Plus, Edit, Trash2, Star, ChevronRight, Gift, Loader2,
  CheckCircle, Clock, Truck, XCircle, ShoppingCart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { userAPI, authAPI, wishlistAPI, cartAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const AccountPage = () => {
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();
  const [activeTab, setActiveTab] = useState('orders');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    phone: ''
  });
  
  // Address modal state
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    label: 'Home',
    first_name: '',
    last_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'United States',
    phone: '',
    is_default: false
  });

  const menuItems = [
    { id: 'orders', label: 'My Orders', icon: Package },
    { id: 'addresses', label: 'Addresses', icon: MapPin },
    { id: 'rewards', label: 'Rewards', icon: Award },
    { id: 'wishlist', label: 'Wishlist', icon: Heart },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  useEffect(() => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    let isMounted = true;
    
    const fetchUserData = async () => {
      try {
        const userId = localStorage.getItem('user_id') || 'guest';
        const [userRes, ordersRes, addressesRes, loyaltyRes, wishlistRes] = await Promise.all([
          userAPI.getProfile().catch(() => null),
          userAPI.getMyOrders().catch(() => ({ data: [] })),
          userAPI.getAddresses().catch(() => ({ data: [] })),
          userAPI.getLoyalty().catch(() => ({ data: null })),
          wishlistAPI.getProducts(userId).catch(() => ({ data: [] }))
        ]);
        
        if (isMounted) {
          if (userRes?.data) {
            setUser(userRes.data);
            setProfileForm({
              first_name: userRes.data.first_name || '',
              last_name: userRes.data.last_name || '',
              phone: userRes.data.phone || ''
            });
          }
          setOrders(ordersRes?.data || []);
          setAddresses(addressesRes?.data || []);
          setLoyalty(loyaltyRes?.data || null);
          setWishlistItems(wishlistRes?.data || []);
        }
      } catch (error) {
        if (isMounted && error.name !== 'CanceledError') {
          console.error('Error fetching user data:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchUserData();
    
    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const fetchUserData = async () => {
    try {
      const userId = localStorage.getItem('user_id') || 'guest';
      const [userRes, ordersRes, addressesRes, loyaltyRes, wishlistRes] = await Promise.all([
        userAPI.getProfile().catch(() => null),
        userAPI.getMyOrders().catch(() => ({ data: [] })),
        userAPI.getAddresses().catch(() => ({ data: [] })),
        userAPI.getLoyalty().catch(() => ({ data: null })),
        wishlistAPI.getProducts(userId).catch(() => ({ data: [] }))
      ]);
      
      if (userRes?.data) {
        setUser(userRes.data);
        setProfileForm({
          first_name: userRes.data.first_name || '',
          last_name: userRes.data.last_name || '',
          phone: userRes.data.phone || ''
        });
      }
      setOrders(ordersRes?.data || []);
      setAddresses(addressesRes?.data || []);
      setLoyalty(loyaltyRes?.data || null);
      setWishlistItems(wishlistRes?.data || []);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await authLogout();
    localStorage.removeItem('token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    navigate('/login');
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await userAPI.updateProfile(profileForm);
      setUser(prev => ({ ...prev, ...profileForm }));
      toast.success('Profile Updated', {
        description: 'Your profile has been saved successfully.'
      });
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to update profile'
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    try {
      const userId = localStorage.getItem('user_id') || 'guest';
      await wishlistAPI.remove(userId, productId);
      setWishlistItems(prev => prev.filter(item => item._id !== productId));
      toast.success('Removed from Wishlist', {
        description: 'Item has been removed from your wishlist.'
      });
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to remove item'
      });
    }
  };

  const handleAddToCartFromWishlist = async (product) => {
    try {
      const userId = localStorage.getItem('user_id') || 'guest';
      await cartAPI.addItem(userId, product._id, 1);
      toast.success('Added to Cart!', {
        description: `${product.name} has been added to your cart.`,
        action: {
          label: 'View Cart',
          onClick: () => window.location.href = '/cart'
        }
      });
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to add to cart'
      });
    }
  };

  const handleRedeemPoints = async (points) => {
    try {
      await userAPI.redeemPoints({ points });
      fetchUserData();
      toast.success('Points Redeemed!', {
        description: `You've redeemed ${points} points for store credit.`
      });
    } catch (error) {
      toast.error('Error', {
        description: error.response?.data?.detail || 'Failed to redeem points'
      });
    }
  };

  const handleAddAddress = () => {
    setEditingAddress(null);
    setAddressForm({
      label: 'Home',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'United States',
      phone: user?.phone || '',
      is_default: addresses.length === 0
    });
    setShowAddressModal(true);
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setAddressForm({
      label: address.label,
      first_name: address.first_name,
      last_name: address.last_name,
      address_line1: address.address_line1,
      address_line2: address.address_line2 || '',
      city: address.city,
      state: address.state,
      postal_code: address.postal_code,
      country: address.country,
      phone: address.phone,
      is_default: address.is_default
    });
    setShowAddressModal(true);
  };

  const handleSaveAddress = async () => {
    try {
      if (editingAddress) {
        await userAPI.updateAddress(editingAddress._id, addressForm);
      } else {
        await userAPI.createAddress(addressForm);
      }
      setShowAddressModal(false);
      fetchUserData();
    } catch (error) {
      alert('Failed to save address');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (window.confirm('Delete this address?')) {
      try {
        await userAPI.deleteAddress(addressId);
        fetchUserData();
      } catch (error) {
        alert('Failed to delete address');
      }
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      await userAPI.setDefaultAddress(addressId);
      fetchUserData();
    } catch (error) {
      alert('Failed to set default address');
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'shipped': return <Truck className="h-5 w-5 text-blue-600" />;
      case 'paid': case 'new': return <Clock className="h-5 w-5 text-amber-600" />;
      case 'refunded': case 'cancelled': return <XCircle className="h-5 w-5 text-red-600" />;
      default: return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'shipped': return 'bg-blue-100 text-blue-800';
      case 'paid': case 'new': return 'bg-amber-100 text-amber-800';
      case 'refunded': case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-main">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-text-heading font-heading">My Account</h1>
          {user && (
            <p className="text-lg text-text-body font-body mt-2">
              Welcome back, {user.first_name || user.email}!
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 border border-soft-sand shadow-soft sticky top-8">
              {/* User Info */}
              {user && (
                <div className="flex items-center gap-4 pb-6 mb-6 border-b border-soft-sand">
                  <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center">
                    <User className="h-7 w-7 text-accent" />
                  </div>
                  <div>
                    <p className="font-semibold text-text-heading font-body">
                      {user.first_name} {user.last_name}
                    </p>
                    <p className="text-sm text-text-muted font-body">{user.email}</p>
                  </div>
                </div>
              )}

              {/* Points Badge */}
              {loyalty && (
                <div className="bg-gradient-to-r from-accent to-soft-terracotta rounded-xl p-4 mb-6 text-white">
                  <div className="flex items-center gap-2 mb-1">
                    <Star className="h-5 w-5" />
                    <span className="text-sm font-body">{loyalty.tier} Member</span>
                  </div>
                  <p className="text-2xl font-bold font-heading">{loyalty.points} pts</p>
                </div>
              )}

              {/* Navigation */}
              <nav className="space-y-1">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors font-body ${
                        activeTab === item.id
                          ? 'bg-accent text-white'
                          : 'text-text-body hover:bg-soft-sand'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      {item.label}
                    </button>
                  );
                })}
              </nav>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 mt-4 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-body"
              >
                <LogOut className="h-5 w-5" />
                Sign Out
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* ORDERS TAB */}
            {activeTab === 'orders' && (
              <div className="bg-white rounded-2xl p-8 border border-soft-sand shadow-soft">
                <h2 className="text-2xl font-bold text-text-heading mb-6 font-heading">My Orders</h2>
                
                {orders.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="h-16 w-16 mx-auto text-text-muted mb-4" />
                    <h3 className="text-xl font-semibold text-text-heading mb-2 font-heading">No orders yet</h3>
                    <p className="text-text-body mb-6 font-body">Start shopping to see your orders here</p>
                    <Link to="/shop">
                      <Button className="bg-accent hover:bg-accent-hover text-white">
                        Browse Products
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order._id} className="border border-soft-sand rounded-xl p-6 hover:border-accent transition-colors">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              {getStatusIcon(order.status)}
                              <p className="font-semibold text-text-heading font-body">
                                Order #{order.order_number}
                              </p>
                            </div>
                            <p className="text-sm text-text-muted font-body">
                              {new Date(order.created_at).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'long', day: 'numeric'
                              })}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium font-body ${getStatusColor(order.status)}`}>
                              {order.status}
                            </span>
                            <p className="text-xl font-bold text-text-heading font-heading">
                              ${order.total?.toFixed(2)}
                            </p>
                          </div>
                        </div>
                        {order.items && order.items.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-soft-sand">
                            <p className="text-sm text-text-muted font-body">
                              {order.items.length} item{order.items.length > 1 ? 's' : ''}
                            </p>
                          </div>
                        )}
                        {order.tracking_number && (
                          <div className="mt-2">
                            <p className="text-sm text-text-body font-body">
                              Tracking: <span className="font-mono">{order.tracking_number}</span>
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ADDRESSES TAB */}
            {activeTab === 'addresses' && (
              <div className="bg-white rounded-2xl p-8 border border-soft-sand shadow-soft">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-text-heading font-heading">My Addresses</h2>
                  <Button onClick={handleAddAddress} className="bg-accent hover:bg-accent-hover text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Address
                  </Button>
                </div>
                
                {addresses.length === 0 ? (
                  <div className="text-center py-12">
                    <MapPin className="h-16 w-16 mx-auto text-text-muted mb-4" />
                    <h3 className="text-xl font-semibold text-text-heading mb-2 font-heading">No addresses saved</h3>
                    <p className="text-text-body font-body">Add an address for faster checkout</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {addresses.map((address) => (
                      <div key={address._id} className={`border rounded-xl p-6 ${address.is_default ? 'border-accent bg-accent/5' : 'border-soft-sand'}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-semibold text-text-heading font-body">{address.label}</span>
                              {address.is_default && (
                                <span className="text-xs px-2 py-1 bg-accent text-white rounded-full">Default</span>
                              )}
                            </div>
                            <p className="text-text-body font-body">
                              {address.first_name} {address.last_name}
                            </p>
                            <p className="text-text-body font-body">{address.address_line1}</p>
                            {address.address_line2 && (
                              <p className="text-text-body font-body">{address.address_line2}</p>
                            )}
                            <p className="text-text-body font-body">
                              {address.city}, {address.state} {address.postal_code}
                            </p>
                            <p className="text-text-muted font-body text-sm mt-1">{address.phone}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {!address.is_default && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleSetDefault(address._id)}
                                className="text-accent hover:bg-accent/10"
                              >
                                Set Default
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => handleEditAddress(address)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => handleDeleteAddress(address._id)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* REWARDS TAB */}
            {activeTab === 'rewards' && (
              <div className="space-y-6">
                {/* Points Overview */}
                <div className="bg-gradient-to-br from-accent via-accent to-soft-terracotta rounded-2xl p-8 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <Award className="h-8 w-8" />
                    <span className="text-xl font-body">{loyalty?.tier || 'Bronze'} Member</span>
                  </div>
                  <p className="text-6xl font-bold font-heading mb-2">{loyalty?.points || 0}</p>
                  <p className="text-lg font-body opacity-90">Available Points</p>
                  
                  {loyalty?.next_tier_points > 0 && (
                    <div className="mt-6 pt-6 border-t border-white/20">
                      <p className="text-sm font-body mb-2">
                        {loyalty.next_tier_points} points to next tier
                      </p>
                      <div className="h-2 bg-white/30 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-white rounded-full"
                          style={{ 
                            width: `${Math.min(100, ((loyalty.points / (loyalty.points + loyalty.next_tier_points)) * 100))}%` 
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Benefits */}
                <div className="bg-white rounded-2xl p-8 border border-soft-sand shadow-soft">
                  <h3 className="text-xl font-bold text-text-heading mb-6 font-heading flex items-center gap-2">
                    <Gift className="h-5 w-5 text-accent" />
                    Your Benefits
                  </h3>
                  <ul className="space-y-3">
                    {(loyalty?.benefits || [
                      'Earn 1 point per $1 spent',
                      'Birthday bonus: 50 points',
                      'Access to member-only sales'
                    ]).map((benefit, index) => (
                      <li key={index} className="flex items-center gap-3 text-text-body font-body">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Redeem Points */}
                <div className="bg-white rounded-2xl p-8 border border-soft-sand shadow-soft">
                  <h3 className="text-xl font-bold text-text-heading mb-4 font-heading">Redeem Points</h3>
                  <p className="text-text-body mb-4 font-body">
                    Convert your points to store credit. 100 points = $5 credit.
                  </p>
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      disabled={(loyalty?.points || 0) < 100}
                      className="border-accent text-accent hover:bg-accent hover:text-white"
                      onClick={() => handleRedeemPoints(100)}
                    >
                      Redeem 100 pts ($5)
                    </Button>
                    <Button 
                      variant="outline" 
                      disabled={(loyalty?.points || 0) < 500}
                      className="border-accent text-accent hover:bg-accent hover:text-white"
                      onClick={() => handleRedeemPoints(500)}
                    >
                      Redeem 500 pts ($25)
                    </Button>
                  </div>
                </div>

                {/* Points History */}
                {loyalty?.history && loyalty.history.length > 0 && (
                  <div className="bg-white rounded-2xl p-8 border border-soft-sand shadow-soft">
                    <h3 className="text-xl font-bold text-text-heading mb-6 font-heading">Points History</h3>
                    <div className="space-y-3">
                      {loyalty.history.map((item) => (
                        <div key={item._id} className="flex items-center justify-between py-3 border-b border-soft-sand last:border-0">
                          <div>
                            <p className="font-medium text-text-heading font-body">{item.description}</p>
                            <p className="text-sm text-text-muted font-body">
                              {new Date(item.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <span className={`font-bold font-heading ${item.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {item.points > 0 ? '+' : ''}{item.points}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* WISHLIST TAB */}
            {activeTab === 'wishlist' && (
              <div className="bg-white rounded-2xl p-8 border border-soft-sand shadow-soft">
                <h2 className="text-2xl font-bold text-text-heading mb-6 font-heading">My Wishlist</h2>
                {wishlistItems.length === 0 ? (
                  <div className="text-center py-12">
                    <Heart className="h-16 w-16 mx-auto text-text-muted mb-4" />
                    <h3 className="text-xl font-semibold text-text-heading mb-2 font-heading">Your wishlist is empty</h3>
                    <p className="text-text-body mb-6 font-body">Save items you love for later</p>
                    <Link to="/shop">
                      <Button className="bg-accent hover:bg-accent-hover text-white">
                        Discover Products
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {wishlistItems.map((product) => (
                      <div key={product._id} className="flex items-center gap-4 p-4 border border-soft-sand rounded-xl hover:border-accent transition-colors">
                        <Link to={`/shop/product/${product.slug}`} className="flex-shrink-0">
                          <img 
                            src={product.images?.[0] || 'https://via.placeholder.com/100'} 
                            alt={product.name}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link to={`/shop/product/${product.slug}`}>
                            <h4 className="font-semibold text-text-heading font-body truncate hover:text-accent">
                              {product.name}
                            </h4>
                          </Link>
                          <p className="text-sm text-text-muted font-body">{product.category}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {product.sale_price ? (
                              <>
                                <span className="font-bold text-accent font-heading">${product.sale_price.toFixed(2)}</span>
                                <span className="text-sm text-text-muted line-through">${product.price.toFixed(2)}</span>
                              </>
                            ) : (
                              <span className="font-bold text-text-heading font-heading">${product.price?.toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm"
                            className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#0A0A0A] font-bold"
                            onClick={() => handleAddToCartFromWishlist(product)}
                          >
                            <ShoppingCart className="h-4 w-4 mr-1" />
                            Add to Cart
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleRemoveFromWishlist(product._id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* SETTINGS TAB */}
            {activeTab === 'settings' && (
              <div className="bg-white rounded-2xl p-8 border border-soft-sand shadow-soft">
                <h2 className="text-2xl font-bold text-text-heading mb-6 font-heading">Account Settings</h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-text-heading mb-2 font-body">Email</label>
                    <Input value={user?.email || ''} disabled className="bg-soft-sand" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-heading mb-2 font-body">First Name</label>
                      <Input 
                        value={profileForm.first_name} 
                        onChange={(e) => setProfileForm(prev => ({ ...prev, first_name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-heading mb-2 font-body">Last Name</label>
                      <Input 
                        value={profileForm.last_name}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, last_name: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-heading mb-2 font-body">Phone</label>
                    <Input 
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <Button 
                    className="bg-accent hover:bg-accent-hover text-white"
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                  >
                    {savingProfile ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Address Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-text-heading mb-6 font-heading">
              {editingAddress ? 'Edit Address' : 'Add New Address'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-heading mb-1 font-body">Label</label>
                <select
                  value={addressForm.label}
                  onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                  className="w-full px-4 py-2 border border-soft-sand rounded-lg font-body"
                >
                  <option value="Home">Home</option>
                  <option value="Work">Work</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-heading mb-1 font-body">First Name</label>
                  <Input
                    value={addressForm.first_name}
                    onChange={(e) => setAddressForm({ ...addressForm, first_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-heading mb-1 font-body">Last Name</label>
                  <Input
                    value={addressForm.last_name}
                    onChange={(e) => setAddressForm({ ...addressForm, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-heading mb-1 font-body">Address Line 1</label>
                <Input
                  value={addressForm.address_line1}
                  onChange={(e) => setAddressForm({ ...addressForm, address_line1: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-heading mb-1 font-body">Address Line 2 (Optional)</label>
                <Input
                  value={addressForm.address_line2}
                  onChange={(e) => setAddressForm({ ...addressForm, address_line2: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-heading mb-1 font-body">City</label>
                  <Input
                    value={addressForm.city}
                    onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-heading mb-1 font-body">State</label>
                  <Input
                    value={addressForm.state}
                    onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-heading mb-1 font-body">Postal Code</label>
                  <Input
                    value={addressForm.postal_code}
                    onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-heading mb-1 font-body">Phone</label>
                  <Input
                    value={addressForm.phone}
                    onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={addressForm.is_default}
                  onChange={(e) => setAddressForm({ ...addressForm, is_default: e.target.checked })}
                  className="w-5 h-5 text-accent"
                />
                <span className="font-body text-text-body">Set as default address</span>
              </label>
            </div>
            <div className="flex gap-4 mt-8">
              <Button variant="outline" className="flex-1" onClick={() => setShowAddressModal(false)}>
                Cancel
              </Button>
              <Button className="flex-1 bg-accent hover:bg-accent-hover text-white" onClick={handleSaveAddress}>
                {editingAddress ? 'Update' : 'Save'} Address
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountPage;
